/**
 * Firebase Phone Authentication Hook
 *
 * Handles the Firebase phone auth flow:
 * 1. Setup invisible reCAPTCHA
 * 2. Send OTP via Firebase
 * 3. Verify OTP with Firebase
 * 4. Exchange Firebase token for app JWT
 *
 * Usage:
 *   const { sendOTP, verifyOTP, loading, error } = useFirebaseAuth();
 *
 *   // Send OTP
 *   await sendOTP('+251912345678');
 *
 *   // Verify OTP (returns app auth data)
 *   const authData = await verifyOTP('123456');
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signInWithPopup,
  GoogleAuthProvider,
  ConfirmationResult,
  signOut,
} from 'firebase/auth';
import { getFirebaseAuth, isFirebaseConfigured } from '../config/firebase';
import { loadStoredAcquisition } from './useAcquisitionTracking';

/**
 * Build the body for /api/auth/firebase/verify, splicing in any first-touch
 * acquisition we captured during the visit so the brand-new student row
 * gets attribution. Returning users have their existing acquisition kept;
 * the API only writes these fields on student creation.
 */
function buildVerifyBody(idToken: string): Record<string, unknown> {
  const stored = loadStoredAcquisition();
  return {
    id_token: idToken,
    acquisition_source: stored?.source ?? null,
    acquisition_campaign: stored?.campaign ?? null,
    acquisition_payload: stored?.raw_query ?? null,
  };
}

interface FirebaseAuthResult {
  accessToken: string;
  studentId: string;
  isNewUser: boolean;
  requiresRegistration: boolean;
}

interface UseFirebaseAuthReturn {
  sendOTP: (phoneNumber: string, buttonId?: string) => Promise<boolean>;
  verifyOTP: (otp: string) => Promise<FirebaseAuthResult | null>;
  signInWithGoogle: () => Promise<FirebaseAuthResult | null>;
  signOutFirebase: () => Promise<void>;
  loading: boolean;
  error: string | null;
  clearError: () => void;
  isConfigured: boolean;
}

export function useFirebaseAuth(): UseFirebaseAuthReturn {
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Use ref instead of state to avoid cleanup effect triggering on state change
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  const isConfigured = isFirebaseConfigured();

  // Cleanup reCAPTCHA only on unmount
  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          // Ignore - verifier may already be destroyed
        }
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Setup invisible reCAPTCHA verifier
   */
  const setupRecaptcha = useCallback((buttonId: string): RecaptchaVerifier | null => {
    const auth = getFirebaseAuth();
    if (!auth) {
      setError('Firebase not configured');
      return null;
    }

    // Clear existing verifier safely
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear();
      } catch (e) {
        // Ignore - verifier may already be destroyed
      }
      recaptchaVerifierRef.current = null;
    }

    try {
      const verifier = new RecaptchaVerifier(auth, buttonId, {
        size: 'invisible',
        callback: () => {
          // reCAPTCHA solved - will proceed with signInWithPhoneNumber
          console.log('reCAPTCHA verified');
        },
        'expired-callback': () => {
          setError('reCAPTCHA expired. Please try again.');
        },
      });

      recaptchaVerifierRef.current = verifier;
      return verifier;
    } catch (err: any) {
      console.error('Failed to setup reCAPTCHA:', err);
      setError('Failed to initialize verification. Please refresh the page.');
      return null;
    }
  }, []);

  /**
   * Send OTP to phone number via Firebase
   */
  const sendOTP = useCallback(async (
    phoneNumber: string,
    buttonId: string = 'send-otp-button'
  ): Promise<boolean> => {
    const auth = getFirebaseAuth();
    if (!auth) {
      setError('Firebase not configured');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Format phone number (ensure E.164 format)
      let formattedPhone = phoneNumber.trim();
      if (!formattedPhone.startsWith('+')) {
        // Assume Ethiopian number
        if (formattedPhone.startsWith('0')) {
          formattedPhone = '+251' + formattedPhone.substring(1);
        } else {
          formattedPhone = '+251' + formattedPhone;
        }
      }

      // For Firebase test phone numbers, disable reCAPTCHA verification.
      // Test numbers are configured in Firebase Console and don't send real SMS.
      // This allows automated testing tools to authenticate without being blocked.
      const testNumbers = (process.env.REACT_APP_FIREBASE_TEST_PHONES || '').split(',').filter(Boolean);
      const isTestNumber = testNumbers.some(tn => formattedPhone.endsWith(tn.trim()));

      if (isTestNumber) {
        auth.settings.appVerificationDisabledForTesting = true;
      } else {
        auth.settings.appVerificationDisabledForTesting = false;
      }

      // Setup reCAPTCHA verifier (required by signInWithPhoneNumber signature).
      // For test numbers, Firebase skips the actual challenge silently.
      const verifier = setupRecaptcha(buttonId);
      if (!verifier) {
        setLoading(false);
        return false;
      }

      console.log('Sending OTP to:', formattedPhone);

      const result = await signInWithPhoneNumber(auth, formattedPhone, verifier);
      setConfirmationResult(result);

      console.log('OTP sent successfully');
      return true;
    } catch (err: any) {
      console.error('Failed to send OTP:', err);

      // Handle specific Firebase errors
      switch (err.code) {
        case 'auth/invalid-phone-number':
          setError('Invalid phone number format');
          break;
        case 'auth/too-many-requests':
          setError('Too many attempts. Please try again later.');
          break;
        case 'auth/captcha-check-failed':
          setError('Verification failed. Please try again.');
          break;
        case 'auth/quota-exceeded':
          setError('SMS quota exceeded. Please try again later.');
          break;
        default:
          setError(err.message || 'Failed to send OTP');
      }

      return false;
    } finally {
      setLoading(false);
    }
  }, [setupRecaptcha]);

  /**
   * Verify OTP and exchange Firebase token for app JWT
   */
  const verifyOTP = useCallback(async (otp: string): Promise<FirebaseAuthResult | null> => {
    if (!confirmationResult) {
      setError('Please request OTP first');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      // Verify OTP with Firebase
      const result = await confirmationResult.confirm(otp);
      console.log('Firebase verification successful');

      // Get Firebase ID token
      const idToken = await result.user.getIdToken();
      console.log('Got Firebase ID token');

      // Exchange Firebase token for app JWT
      const response = await fetch('/api/auth/firebase/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildVerifyBody(idToken)),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Authentication failed');
      }

      const data = await response.json();
      console.log('Backend verification successful');

      // Store tokens
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('student_id', data.student_id);
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }
      }

      return {
        accessToken: data.access_token,
        studentId: data.student_id,
        isNewUser: data.is_new_user,
        requiresRegistration: data.requires_registration,
      };
    } catch (err: any) {
      console.error('Failed to verify OTP:', err);

      // Handle specific Firebase errors
      switch (err.code) {
        case 'auth/invalid-verification-code':
          setError('Invalid OTP. Please try again.');
          break;
        case 'auth/code-expired':
          setError('OTP expired. Please request a new one.');
          break;
        default:
          setError(err.message || 'Verification failed');
      }

      return null;
    } finally {
      setLoading(false);
    }
  }, [confirmationResult]);

  /**
   * Sign in with Google via Firebase popup.
   *
   * Pops a Google consent screen, gets a Firebase ID token wrapping the
   * Google identity, then exchanges it for a Lomi JWT through the existing
   * /auth/firebase/verify endpoint (which now handles both phone and
   * google.com providers).
   */
  const signInWithGoogle = useCallback(async (): Promise<FirebaseAuthResult | null> => {
    const auth = getFirebaseAuth();
    if (!auth) {
      setError('Firebase not configured');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const provider = new GoogleAuthProvider();
      // Always show account chooser, even if a single account is signed in.
      provider.setCustomParameters({ prompt: 'select_account' });

      const result = await signInWithPopup(auth, provider);
      console.log('Google sign-in popup completed');

      // Get Firebase ID token (wraps the Google identity)
      const idToken = await result.user.getIdToken();
      console.log('Got Firebase ID token from Google sign-in');

      // Exchange for Lomi JWT through the existing Firebase verify endpoint
      const response = await fetch('/api/auth/firebase/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildVerifyBody(idToken)),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Backend authentication failed');
      }

      const data = await response.json();
      console.log('Google → Firebase → Lomi JWT exchange successful');

      // Persist tokens
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('student_id', data.student_id);
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }
      }

      return {
        accessToken: data.access_token,
        studentId: data.student_id,
        isNewUser: data.is_new_user,
        requiresRegistration: data.requires_registration,
      };
    } catch (err: any) {
      console.error('Google sign-in failed:', err);

      switch (err.code) {
        case 'auth/popup-closed-by-user':
          setError(null); // User closed the popup — silent
          return null;
        case 'auth/popup-blocked':
          setError('Popup was blocked. Please allow popups and try again.');
          break;
        case 'auth/cancelled-popup-request':
          setError(null); // Multiple popups — silent
          return null;
        case 'auth/account-exists-with-different-credential':
          setError(
            'An account with this email already exists with a different sign-in method.'
          );
          break;
        case 'auth/network-request-failed':
          setError('Network error. Check your connection and try again.');
          break;
        default:
          setError(err.message || 'Google sign-in failed');
      }

      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Sign out from Firebase
   */
  const signOutFirebase = useCallback(async () => {
    const auth = getFirebaseAuth();
    if (auth) {
      try {
        await signOut(auth);
        console.log('Signed out from Firebase');
      } catch (err) {
        console.error('Failed to sign out from Firebase:', err);
      }
    }

    // Clear local storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('student_id');

    // Reset state
    setConfirmationResult(null);
    setError(null);
  }, []);

  return {
    sendOTP,
    verifyOTP,
    signInWithGoogle,
    signOutFirebase,
    loading,
    error,
    clearError,
    isConfigured,
  };
}

export default useFirebaseAuth;
